import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { GoogleAuthProvider } from 'firebase/auth';
import { Constants } from 'src/app/shared/constant/constants';
import { ToastService, SessionManagerService, LoaderService } from 'src/app/shared/services/index';
import { RequestService } from 'src/app/shared/services/htttp/request.service';
import { IUserDeatils } from 'src/app/shared/models/index';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { UserAuthService } from './user-auth.service';
import { TermsConditionPopupComponent } from '../terms-condition-popup/terms-condition-popup.component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { Router } from '@angular/router';
import { SubscriptionExpirePopupComponent } from '../login/subscription-expire-popup/subscription-expire-popup.component';
import { encryptPassword } from 'src/app/shared/services/utils';
import { Observable, Subject } from 'rxjs';
import { GoogleAccessTokenService } from './google-access-token.service';
import { AccessCodeModalComponent } from '../sign-up/access-code-modal/access-code-modal.component';
import { UserTypes } from '@app/shared/enums/user.type';
import { determineCreatedByValue } from '@app/shared/helper/determine-created-by';
import { USER_HOME } from '@app/shared/constant/routes';
import { TranslateService } from '@ngx-translate/core';
import { THEMES, THEME_IDS } from '@app/shared/services/theme/themes';
import { ThemeService } from '@app/shared/services/theme/theme.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { IUserPreferences } from '@app/shared/models/IUserDetails';

@Injectable({
  providedIn: 'root',
})
export class FirebaseAuthService {
  userId!: string | undefined;
  public modalRef!: BsModalRef;
  socialData: { [key: string]: any } = {};
  private signupDataSubject: Subject<any> = new Subject<any>();
  signupData$: Observable<any> = this.signupDataSubject.asObservable();

  public auth2: any;
  isTabActive = true;
  private intervalId: any;
  private baseUrl: string;

  constructor(
    private angularFireAuth: AngularFireAuth,
    private flashMessage: ToastService,
    private sessionManagerService: SessionManagerService,
    private requestService: RequestService,
    private userAuthService: UserAuthService,
    private angularFirestore: AngularFirestore,
    private readonly modalService: BsModalService,
    private router: Router,
    private loader: LoaderService,
    private googleAccessTokenService: GoogleAccessTokenService,
    private themeService: ThemeService,
    private translateService: TranslateService,
    private readonly http: HttpClient,
  ) {
    this.baseUrl = environment.baseApiURL;
    this.userId = this.sessionManagerService.getFirebaseUserId();
  }

  // Sign in with Google
  googleAuth(type: string, userType = '', parentEmail = '', selectedAccountType = '') {
    return this.authLogin(new GoogleAuthProvider(), type, userType, parentEmail, selectedAccountType);
  }

  // Auth logic to run auth providers
  authLogin(provider: GoogleAuthProvider, type: string, userType = '', parentEmail = '', selectedAccountType = '') {
    provider.setCustomParameters({
      prompt: 'select_account',
    });
    return this.angularFireAuth
      .signInWithPopup(provider)
      .then((result: any) => {
        // Set Google Provider details for validation
        const providerId = result.additionalUserInfo;
        const gProvider = providerId?.providerId;
        this.sessionManagerService.setGProviderDetails(gProvider);
        this.SetUserData(result, Constants.SOCIAL_LOGIN_TYPES.GOOGLE, type, userType, parentEmail, selectedAccountType);
      })
      .catch(error => {
        window.alert(error);
      });
  }

  checkFirebaseAuth() {
    this.angularFireAuth.onAuthStateChanged(user => {
      if (!user) {
        // No user is signed in.
        // login to firebase
        if (this.sessionManagerService.getToken()) {
          this.loginWithToken(this.sessionManagerService.getFirebaseRefreshToken());
        }
      } else {
        this.userId = user.uid;
        this.sessionManagerService.setFirebaseRefreshToken(user?.refreshToken);
        this.sessionManagerService.setFirebaseUserId(user.uid);
      }
    });
  }

  async loginWithToken(token: string, loggedInData?: any) {
    try {
      const firebaseResponse = (await this.angularFireAuth.signInWithCustomToken(token)) as any;

      if (loggedInData) {
        this.sessionManagerService.setLoggedInData(loggedInData.data);

        if (loggedInData.data.sessionUrl) {
          window.open(loggedInData.data.sessionUrl, '_self');
        } else {
          const isIntegratedWithGoogle = this.sessionManagerService.getisIntegratedWithGoogle() === 'true';
          const isNotGoogleProvider = this.sessionManagerService.getgProviderDetails() !== 'google.com';

          if (isIntegratedWithGoogle && isNotGoogleProvider) {
            this.googleAccessTokenService.integrateGoogleScopePermissions();

            let interval: any;
            // Handle Google auth failure to clear interval
            this.googleAccessTokenService.onGoogleAuthFail(() => {
              if (interval) {
                clearInterval(interval); // Clear the interval on failure
              }
            });

            // Bounded: if the Google popup is blocked or dismissed (common on
            // iPad) the token never arrives, and an unbounded wait here used to
            // strand the user on a blank screen. Give up after 20s and let the
            // reconnect banner handle it.
            const accessTokenCheck = new Promise<void>(resolve => {
              let attempts = 0;
              interval = setInterval(() => {
                attempts += 1;
                const accessToken = this.sessionManagerService.getAccessToken();
                if (accessToken || attempts >= 20) {
                  clearInterval(interval);
                  resolve();
                }
              }, 1000);
            });

            await accessTokenCheck;
          }

          this.userAuthService.navigationBasedOnUser(loggedInData.data);
        }

        this.sessionManagerService.setFirebaseUserId(firebaseResponse.user?.uid as string);
      }
    } catch (error) {
      console.error('Login failed:', error);
    }
  }

  //Logout from firebase
  logOutfromFirebase() {
    return this.angularFireAuth.signOut();
  }

  //Set social data model for social logins
  SetUserData(result: any, socialType: string, type: string, userType = '', parentEmail = '', selectedAccountType = '') {
    const priceId = this.sessionManagerService.getPriceId();
    this.socialData = {
      email: result.additionalUserInfo.profile.email,
      username: result.additionalUserInfo.profile.name,
      social: {
        accessKey: result.credential.idToken,
        type: socialType,
      },
    };

    //Set Access Token Expiration Time
    this.sessionManagerService.setFirebaseAccessTokenExpirationTime(result.user.toJSON().stsTokenManager.expirationTime);

    if (type === 'Studentlogin') {
      this.socialData['priceId'] = priceId ? priceId : 'free_trial';
      delete this.socialData.username;
      return this.userSocialLogin(this.socialData);
    } else if (type === 'Schoollogin') {
      delete this.socialData.priceId;
      delete this.socialData.username;
      return this.schoolSocialLogin(this.socialData);
    } else if (type === 'signup') {
      delete this.socialData.username;

      this.socialData['parentEmail'] = parentEmail || null;
      this.socialData['isTermsAndConditionChecked'] = true;
      this.socialData['isUserUnderAge'] = false;
      this.socialData['role'] = Constants.ROLE_IDS.USER;
      this.socialData['userType'] = userType === UserTypes.CHILD ? '1' : userType;
      this.socialData['createdBy'] = determineCreatedByValue(userType, selectedAccountType);
      this.socialData['userPreferences'] = {
        language: 'es',
      };

      return this.socialSignupAndShowAccessModal(this.socialData);
    } else if (type == 'schoolsigup') {
      return this.signupDataSubject.next(this.socialData);
    }
  }

  //User Social login api call
  userSocialLogin(socialData: object) {
    this.loader.start();
    return this.requestService.post<{ data: IUserDeatils }>(Constants.ENDPOINTS.socialLogin, socialData).subscribe(
      async (res: any) => {
        this.loader.stop();
        if (res.data && res.data.activeToken) {
          localStorage.setItem('preferences', JSON.stringify(res.data?.userPreferences));
          await this.updateThemeToContrastTheme(res);

          const isSubscriptionMandatory = res.data.isSubscriptionMandatory == false ? '0' : '1';
          this.sessionManagerService.createSession(
            res.data.activeToken,
            res.data.role,
            res.data.id,
            res.data.levelId,
            res.data.levelNo,
            res.data.email,
          );
          this.sessionManagerService.setLoggedInData(res.data);
          this.sessionManagerService.setSubscriptionInfo(res.data?.subscriptionInfo);
          this.sessionManagerService.setStudentLicenseEndDate(res.data?.studentLicenseEndDate);
          this.sessionManagerService.setStudentSubscriptionType(res.data?.subscriptionType);
          this.sessionManagerService.setPlanInfo(res.data?.planInfo);
          this.sessionManagerService.setIsSubcrpition(isSubscriptionMandatory);
          this.sessionManagerService.setisIntegratedWithGoogle(res.data?.isGoogleIntegrated);
          // this.translateService.use(res?.data?.userPreferences.language || LANGUAGES.ENGLISH);
          // localStorage.setItem('lang', res?.data?.userPreferences?.language || LANGUAGES.ENGLISH);
          if (res.data.sessionUrl) {
            window.open(res.data.sessionUrl, '_self');
          } else {
            const tempIsIntegratedWithGoogle = this.sessionManagerService.getisIntegratedWithGoogle();
            const tempIsGoogleProvider = this.sessionManagerService.getgProviderDetails();
            if (tempIsIntegratedWithGoogle == 'true' && tempIsGoogleProvider == 'google.com') {
              this.googleAccessTokenService.integrateGoogleScopePermissions();
              // Navigation must not depend on the popup succeeding: on iPad it
              // can be blocked, and this interval previously ran forever and
              // left the user stuck on the login screen.
              let accessCodeAttempts = 0;
              let hasNavigated = false;
              const proceed = () => {
                if (hasNavigated) return;
                hasNavigated = true;
                clearInterval(setIntervalForAccessCode);
                this.userAuthService.navigationBasedOnUser(res.data);
              };
              const setIntervalForAccessCode = setInterval(() => {
                accessCodeAttempts += 1;
                const accessToken = this.sessionManagerService.getAccessToken();
                if (accessToken || accessCodeAttempts >= 20) {
                  proceed();
                }
              }, 1000);
              // Popup refused or dismissed: go in anyway, the reconnect banner
              // covers Google access from here.
              this.googleAccessTokenService.onGoogleAuthFail(proceed);
            } else {
              this.userAuthService.navigationBasedOnUser(res.data);
            }
          }
        }
      },
      error => {
        this.loader.stop();
        if (error.error.code == Constants.ERROR_CODES.BAD_REQUEST) {
          const initialState = {
            token: error.error.data.activeToken,
            type: Constants.SOCIAL_LOGIN_TYPES.GOOGLE_SIGNIN,
          };
          this.modalService.show(TermsConditionPopupComponent, {
            initialState,
          });
        } else if (error.error.status == Constants.ERROR_CODES.CONFLICT) {
          this.flashMessage.showError(error.error.errorMessage);
          this.sessionManagerService.setPriceId('free_trial' as string);
          this.router.navigate([USER_HOME.URL]);
        } else if (error.error.status == Constants.ERROR_CODES.TRANSACTION) {
          const initialState = {
            headerText: error.error.errorMessage,
            email: this.socialData.email,
            password: encryptPassword('Hello world'),
            type: true,
          };
          this.modalService.show(SubscriptionExpirePopupComponent, {
            initialState,
          });
        } else {
          this.flashMessage.showError(error.error.errorMessage);
        }
      },
    );
  }

  //School Social login api call
  async updateThemeToContrastTheme(res: any) {
    const { userPreferences, activeToken }: { userPreferences: IUserPreferences & { _id: string; userRole: number }; activeToken: string } = res.data;
    if (userPreferences && res.data?.userPreferences.theme) {
      if (userPreferences?.isUseContrastColors && (!userPreferences?.theme || userPreferences?.theme !== THEME_IDS.DEFAULT_HIGHCONTRAST)) {
        // * Update the theme to constrast theme.
        if (userPreferences?._id) {
          delete (userPreferences as Partial<typeof userPreferences>)._id;
        }
        try {
          const result: any = await this.http
            .patch(
              `${this.baseUrl}${Constants.ENDPOINTS.userPreferencesSet}`,
              {
                ...userPreferences,
                theme: THEME_IDS.DEFAULT_HIGHCONTRAST,
                isUseContrastColors: false,
                userRole: userPreferences.userRole ? userPreferences.userRole : 1,
              },
              {
                headers: {
                  token: activeToken,
                  'X-Skip-Interceptor': 'true',
                },
              },
            )
            .toPromise();
          localStorage.setItem(
            'preferences',
            JSON.stringify({
              ...userPreferences,
              theme: result.data?.theme,
              isUseContrastColors: result.data?.isUseContrastColors,
            }),
          );

          const theme = THEMES.find(color => color.id === result.data?.theme) || THEMES[0];
          this.themeService.setColor(theme);
        } catch (error) {
          console.error('Error updating theme:', error);
        }
      } else {
        const theme = THEMES.find(color => color.id === res.data?.userPreferences.theme);
        this.themeService.setColor(theme! || THEMES[0]);
      }
    } else {
      this.themeService.setColor(THEMES[0]);
    }
  }

  schoolSocialLogin(socialData: object) {
    this.loader.start();
    return this.requestService.post<{ data: IUserDeatils }>(Constants.SCHOOL_ENDPOINTS.socialLogin, socialData).subscribe(
      (res: any) => {
        this.loader.stop();
        if (res.data && res.data.activeToken) {
          this.sessionManagerService.createSession(
            res.data.activeToken,
            res.data.role,
            res.data.id,
            res.data.levelId,
            res.data.levelNo,
            res.data.email,
          );
          this.sessionManagerService.setLoggedInData(res.data);
          if (res.data.sessionUrl) {
            window.open(res.data.sessionUrl, '_self');
          } else {
            this.userAuthService.navigationBasedOnUser(res.data);
          }
        }
      },
      error => {
        this.loader.stop();
        if (error.error.code == Constants.ERROR_CODES.BAD_REQUEST) {
          const initialState = {
            token: error.error.data.activeToken,
            type: Constants.SOCIAL_LOGIN_TYPES.GOOGLE_SIGNIN,
          };
          this.modalService.show(TermsConditionPopupComponent, {
            initialState,
          });
        } else if (error.error.status == Constants.ERROR_CODES.CONFLICT) {
          this.flashMessage.showError(error.error.errorMessage);
          this.sessionManagerService.setPriceId('free_trial' as string);
          this.router.navigate([USER_HOME.URL]);
        } else if (error.error.status == Constants.ERROR_CODES.TRANSACTION) {
          const initialState = {
            headerText: error.error.errorMessage,
            email: this.socialData.email,
            password: encryptPassword('Hello world'),
            type: true,
          };
          this.modalService.show(SubscriptionExpirePopupComponent, {
            initialState,
          });
        } else {
          this.flashMessage.showError(error.error.errorMessage);
        }
      },
    );
  }

  socialSignupAndShowAccessModal(socialData: object) {
    this.loader.start();
    return this.requestService.post<{ data: IUserDeatils }>(Constants.ENDPOINTS.socialSignup, socialData).subscribe(
      (res: any) => {
        this.loader.stop();
        if (res.data && res.data.activeToken) {
          this.modalService.show(AccessCodeModalComponent, {
            ignoreBackdropClick: true,
            initialState: {
              accessToken: res.data.activeToken,
            },
          });
        }
      },
      error => {
        this.loader.stop();
        if (error.error.status == Constants.ERROR_CODES.CONFLICT) {
          this.flashMessage.showError(error.error.errorMessage);
        } else {
          this.flashMessage.showError(error.error.errorMessage);
        }
      },
    );
  }

  //Get users files from firebase
  fileCollection() {
    if (this.userId) {
      return this.angularFirestore.collection('users').doc(this.userId?.toString()).collection('files');
    } else {
      this.userId = this.sessionManagerService.getFirebaseUserId();
      const id: string = this.userId?.toString();
      return this.angularFirestore.collection('users').doc(id).collection('files');
    }
  }

  addData(data: any) {
    return this.fileCollection().add(data);
  }

  //Update file in firebase for a user
  updateDataInFile(data: any, fileId: string): Promise<void> {
    return this.fileCollection().doc(fileId).update(data);
  }

  updateStudentFileAsTeacher(data: any, fileId: string, studentFirebaseId: string): Promise<void> {
    return this.angularFirestore.collection('users').doc(studentFirebaseId).collection('files').doc(fileId).update(data);
  }

  //Get a particular file from firebase files collection for a user
  getData(fileId: string) {
    return this.fileCollection().doc(fileId).ref.get();
  }

  getStudentFile(fileId: string, studentFirebaseId: string) {
    return this.angularFirestore.collection('users').doc(studentFirebaseId).collection('files').doc(fileId).ref.get();
  }

  //Delete file from firebase
  deleteFileData(fileId: string) {
    return this.fileCollection().doc(fileId).delete();
  }

  // Re-Crete Firebase document
  reCreateDataField(fileId: any, data: any) {
    return this.fileCollection().doc(fileId).set(data);
  }
  // Re-Crete Empty Firebase document
  reCreateFirebaseDocument(fileId: any) {
    return this.fileCollection().doc(fileId).set({});
  }

  /**
   * Renew the Google token if it has lapsed.
   *
   * The periodic refresh now lives in `GoogleAccessTokenService`, which owns
   * the token lifecycle and knows when a pop-up is safe to open. The old
   * 55-minute timer here fired regardless of whether the tab was visible,
   * which is what surfaced Google windows to users who had switched away.
   */
  validateGeneratingAccessToken() {
    this.googleAccessTokenService.ensureFreshToken();
  }

  private saveTimestamp(): void {
    const timestamp = Date.now();
    localStorage.setItem('lastActiveTimestamp', `${timestamp}`);
    this.googleAccessTokenService.ensureFreshToken();
  }
}
